import RecruitingStageAccordionList from "./RecruitingStageAccordionList";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import InterviewScheduleInline from "../InterviewScheduleInline";
import {
  Box,
  Typography,
  Card,
  Avatar,
  Divider,
  Chip,
  Button,
  Checkbox,
  Menu,
  MenuItem,
  Link as MuiLink,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { Building, Location, ProfileCircle, Clock } from "iconsax-react";
import { useNavigate } from "react-router-dom";

import { MoveStageActionIcon } from "../ActionButtonIcons";
import JobTabHeader from "./JobTabHeader";
import RejectedStageDialog from "./RejectedStageDialog";
import RejectionAudit from "./RejectionAudit";
import { AccordionHeadingIcon } from "./StageViewAccordionList";
import { MOVE_STAGE_BULK_BUTTON_SX } from "../stageBulkButtonStyles";
import { buildOpenCompanyNavPayload } from "../../utils/companyNavigation";
import {
  fetchRecruiterUsers,
  normalizeTeamMemberName,
} from "../../utils/teamMembers";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const DANGER = "#DC2626";
const TEXT = "#111827";
const MUTED = "#94A3B8";
const NAV_CACHE_KEY = "rpo:openCompanyNav";
const STAGE_ORDER = [
  "New",
  "Applied",
  "Matched",
  "Sourced",
  "Contacted",
  "Screened",
  "Presented",
  "Interview",
  "Assessment",
  "Offer",
  "Hired",
  "Rejected",
];
const MOVE_STAGE_OPTIONS = [
  "New",
  "Applied",
  "Matched",
  "Sourced",
  "Contacted",
  "Screened",
  "Presented",
  "Interview",
  "Assessment",
  "Offer",
  "Hired",
  "Rejected",
];

const normalizeStageLabel = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "all") return "All Stages";
  if (raw === "new") return "New";
  if (raw === "applied") return "Applied";
  if (raw === "matched") return "Matched";
  if (raw === "sourced") return "Sourced";
  if (raw === "contacted") return "Contacted";
  if (raw === "screened") return "Screened";
  if (raw === "assessment" || raw === "assessments") return "Assessment";
  if (raw === "offer") return "Offer";
  if (raw === "hired") return "Hired";
  if (raw === "rejected") return "Rejected";
  if (raw === "interviewing") return "Interview";
  if (raw === "interview") return "Interview";
  if (raw === "presented") return "Presented";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const resolveStageItemLabel = (row = {}) => {
  const explicitStage = String(
    row?.candidate_stage || row?.stage || row?.candidate_status || "New"
  ).trim();

  return normalizeStageLabel(explicitStage) || "New";
};

const normalizeLookupName = (value) =>
  normalizeTeamMemberName(value).toLowerCase();

const getRecruiterProfileId = (recruiterUsersByName, recruiterName) => {
  const lookupKey = normalizeLookupName(recruiterName);
  if (!lookupKey || typeof recruiterUsersByName?.get !== "function") return null;

  const matchedRecruiter = recruiterUsersByName.get(lookupKey);
  const numericId = Number(matchedRecruiter?.id || 0);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
};

const normalizeStageItemText = (value) => String(value || "").trim();

const mapRowToStageItem = (row = {}, recruiterUsersByName = null) => {
  const stageLabel = resolveStageItemLabel(row);
  const numericCandidateId = Number(row?.candidate_id || 0);
  const numericCompanyId = Number(
    row?.candidate_company_id || row?.company_client_id || row?.companyId || 0
  );
  const recruiterName = String(row?.recruiter_name || row?.owner || "").trim();
  const candidateId =
    Number.isFinite(numericCandidateId) && numericCandidateId > 0
      ? numericCandidateId
      : null;
  const companyId =
    Number.isFinite(numericCompanyId) && numericCompanyId > 0 ? numericCompanyId : null;

  return {
    id:
      row?.id ||
      `${row?.job_id || "job"}-${candidateId || row?.candidate_name || "candidate"}`,
    candidateId,
    jobId: row?.job_id,
    companyId,
    stageLabel,
    avatar: row?.avatar_url || "",
    name: row?.candidate_name || "Candidate",
    title: row?.candidate_title || "No title",
    company: row?.current_company || row?.candidate_company || "No recent company",
    location: row?.candidate_location || "Location unavailable",
    owner: recruiterName || "Unassigned",
    recruiterProfileId: recruiterName
      ? getRecruiterProfileId(recruiterUsersByName, recruiterName)
      : null,
    leadStatus: stageLabel,
    rejectedBy: normalizeStageItemText(row?.rejected_by || row?.rejectedBy),
    rejectionAudit: row,
    rejectionReason: normalizeStageItemText(
      row?.rejection_reason || row?.rejectionReason
    ),
    rejectionNote: normalizeStageItemText(row?.rejection_note || row?.rejectionNote),
    tags: [],
  };
};

function RejectionDetails({ item, compact = false }) {
  if (item.rejectionAudit?.outcome === "Rejected" && item.rejectionAudit?.rejected_at) return <RejectionAudit item={item.rejectionAudit} />;
  if (item.stageLabel !== "Rejected") return null;

  const detailParts = [
    item.rejectedBy ? `Rejected by: ${item.rejectedBy}` : "",
    item.rejectionReason ? `Reason: ${item.rejectionReason}` : "",
  ].filter(Boolean);

  if (!detailParts.length && !item.rejectionNote) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: compact ? 0.7 : 1,
        px: compact ? 0.9 : 1,
        py: compact ? 0.75 : 0.9,
        borderRadius: 1.5,
        border: "1px solid #FECACA",
        backgroundColor: "#FEF2F2",
      }}
    >
      {detailParts.length ? (
        <Typography
          sx={{
            fontFamily: "Be Vietnam Pro",
            fontSize: compact ? 10 : 10.5,
            fontWeight: 900,
            color: DANGER,
            lineHeight: 1.35,
          }}
        >
          {detailParts.join(" • ")}
        </Typography>
      ) : null}

      {item.rejectionNote ? (
        <Typography
          sx={{
            mt: detailParts.length ? 0.4 : 0,
            fontFamily: "Be Vietnam Pro",
            fontSize: compact ? 10 : 10.5,
            fontWeight: 800,
            color: "#7F1D1D",
            lineHeight: 1.4,
            whiteSpace: "pre-line",
            wordBreak: "break-word",
          }}
        >
          Note: {item.rejectionNote}
        </Typography>
      ) : null}
    </Box>
  );
}

function StageActionButton({
  children,
  color = PURPLE,
  disabled = false,
  onClick,
}) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        border: 0,
        p: 0,
        m: 0,
        backgroundColor: "transparent",
        fontFamily: "Be Vietnam Pro",
        fontSize: 10.5,
        fontWeight: 900,
        color,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {children}
    </Box>
  );
}

function StageTextLink({ children, onClick, sx = {} }) {
  return (
    <MuiLink
      component="button"
      type="button"
      underline="hover"
      onClick={onClick}
      sx={{
        p: 0,
        border: 0,
        backgroundColor: "transparent",
        color: "inherit",
        fontSize: "inherit",
        fontWeight: "inherit",
        fontFamily: "inherit",
        lineHeight: "inherit",
        textAlign: "left",
        cursor: "pointer",
        textUnderlineOffset: "2px",
        minWidth: 0,
        verticalAlign: "baseline",
        "&:hover": {
          color: "inherit",
        },
        ...sx,
      }}
    >
      {children}
    </MuiLink>
  );
}

function StageCard({
  item,
  checked = false,
  moving = false,
  canDelete = false,
  onToggleSelect,
  onOpenMoveStageMenu,
  onRejectCandidate,
  onDeleteCandidate,
  onOpenCandidateProfile,
  onOpenCompany,
  onOpenRecruiterProfile,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: "#E6EAF2",
        borderRadius: 2,
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Avatar
            src={item.avatar}
            sx={{
              width: 28,
              height: 28,
              bgcolor: "#EEF2FF",
              color: BLUE,
              fontFamily: "Be Vietnam Pro",
              fontWeight: 900,
              fontSize: 12,
              flex: "0 0 auto",
            }}
          >
            {item.name?.[0]}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            {item.candidateId ? (
              <StageTextLink
                onClick={() => onOpenCandidateProfile?.(item.candidateId)}
                sx={{
                  display: "block",
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 11.5,
                  fontWeight: 900,
                  color: BLUE,
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.name}
              </StageTextLink>
            ) : (
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 11.5,
                  fontWeight: 900,
                  color: BLUE,
                  lineHeight: 1.1,
                }}
                noWrap
              >
                {item.name}
              </Typography>
            )}

            <Typography
              sx={{
                mt: 0.4,
                fontFamily: "Be Vietnam Pro",
                fontSize: 10.5,
                fontWeight: 800,
                color: MUTED,
              }}
              noWrap
            >
              {item.title}
            </Typography>
          </Box>

          <Checkbox
            size="small"
            checked={checked}
            disabled={!item.candidateId || moving}
            onChange={() => onToggleSelect?.(item.candidateId)}
            onClick={(event) => event.stopPropagation()}
            sx={{
              p: 0,
              width: 18,
              height: 18,
              mt: 0.2,
              "& .MuiSvgIcon-root": { fontSize: 18 },
              color: "#D0D5DD",
              "&.Mui-checked": { color: PURPLE },
            }}
          />
        </Box>

        <Divider sx={{ my: 1, borderColor: "#EEF2F7" }} />

        <Box sx={{ display: "grid", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Building size="14" color={MUTED} variant="Outline" />
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontSize: 10.5,
                fontWeight: 800,
                color: TEXT,
              }}
              noWrap
            >
              Company:{" "}
              {item.companyId && item.company && item.company !== "No recent company" ? (
                <StageTextLink
                  onClick={() => onOpenCompany?.(item.companyId, item.company)}
                  sx={{ color: BLUE, fontWeight: 900 }}
                >
                  {item.company}
                </StageTextLink>
              ) : (
                item.company
              )}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Location size="14" color={MUTED} variant="Outline" />
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontSize: 10.5,
                fontWeight: 800,
                color: TEXT,
              }}
              noWrap
            >
              Location: {item.location}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <ProfileCircle size="14" color={MUTED} variant="Outline" />
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontSize: 10.5,
                fontWeight: 800,
                color: TEXT,
              }}
              noWrap
            >
              Owner:{" "}
              {item.recruiterProfileId && item.owner && item.owner !== "Unassigned" ? (
                <StageTextLink
                  onClick={() => onOpenRecruiterProfile?.(item.recruiterProfileId)}
                  sx={{ color: BLUE, fontWeight: 900 }}
                >
                  {item.owner}
                </StageTextLink>
              ) : (
                item.owner
              )}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Clock size="14" color={MUTED} variant="Outline" />
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontSize: 10.5,
                fontWeight: 800,
                color: TEXT,
              }}
              noWrap
            >
              Status: {item.leadStatus}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 1.1, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.1 }}>
          <StageActionButton
            disabled={!item.candidateId || moving}
            onClick={(event) => onOpenMoveStageMenu?.(event, item.candidateId)}
          >
            Move Stage
          </StageActionButton>
          <StageActionButton
            color={MUTED}
            disabled={!item.candidateId || moving}
            onClick={() => onRejectCandidate?.(item.candidateId)}
          >
            Reject
          </StageActionButton>
          {canDelete ? (
            <StageActionButton
              color={DANGER}
              disabled={!item.candidateId || moving}
              onClick={() => onDeleteCandidate?.(item.candidateId)}
            >
              Delete
            </StageActionButton>
          ) : null}
        </Box>

        <RejectionDetails item={item} />
        <InterviewScheduleInline candidateId={item.candidateId} jobId={item.jobId} stage={item.stageLabel} />

        {item.tags?.length ? (
          <Box sx={{ mt: 1, display: "flex", gap: 0.6, flexWrap: "wrap" }}>
            {item.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  height: 20,
                  borderRadius: 999,
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 10,
                  fontWeight: 900,
                  color: BLUE,
                  bgcolor: "#EAF2FF",
                }}
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </Card>
  );
}

function StageRow({
  item,
  checked = false,
  moving = false,
  canDelete = false,
  onToggleSelect,
  onOpenMoveStageMenu,
  onRejectCandidate,
  onDeleteCandidate,
  onOpenCandidateProfile,
  onOpenCompany,
  onOpenRecruiterProfile,
}) {
  const hasCompanyText = Boolean(item.company && item.company !== "No recent company");
  const hasLocationText = Boolean(
    item.location && item.location !== "Location unavailable"
  );

  return (
    <Box
      sx={{
        border: "1px solid #E6EAF2",
        borderRadius: 2,
        backgroundColor: "#fff",
        px: 1.25,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.2,
      }}
    >
      <Avatar
        src={item.avatar}
        sx={{
          width: 28,
          height: 28,
          bgcolor: "#EEF2FF",
          color: BLUE,
          fontFamily: "Be Vietnam Pro",
          fontWeight: 900,
          fontSize: 12,
          flex: "0 0 auto",
        }}
      >
        {item.name?.[0]}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        {item.candidateId ? (
          <StageTextLink
            onClick={() => onOpenCandidateProfile?.(item.candidateId)}
            sx={{
              display: "block",
              fontFamily: "Be Vietnam Pro",
              fontSize: 11.5,
              fontWeight: 900,
              color: BLUE,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.name}
          </StageTextLink>
        ) : (
          <Typography
            sx={{
              fontFamily: "Be Vietnam Pro",
              fontSize: 11.5,
              fontWeight: 900,
              color: BLUE,
              lineHeight: 1.1,
            }}
            noWrap
          >
            {item.name}
          </Typography>
        )}
        <Typography
          component="div"
          sx={{
            mt: 0.35,
            fontFamily: "Be Vietnam Pro",
            fontSize: 10.5,
            fontWeight: 800,
            color: MUTED,
          }}
          noWrap
        >
          <Box component="span">{item.title}</Box>
          {hasCompanyText ? <Box component="span"> | </Box> : null}
          {hasCompanyText ? (
            item.companyId ? (
              <StageTextLink
                onClick={() => onOpenCompany?.(item.companyId, item.company)}
                sx={{ color: BLUE, fontWeight: 900 }}
              >
                {item.company}
              </StageTextLink>
            ) : (
              <Box component="span">{item.company}</Box>
            )
          ) : null}
          {hasLocationText ? <Box component="span"> | </Box> : null}
          {hasLocationText ? <Box component="span">{item.location}</Box> : null}
          {item.owner && item.owner !== "Unassigned" ? <Box component="span"> | </Box> : null}
          {item.owner && item.owner !== "Unassigned" ? (
            item.recruiterProfileId ? (
              <StageTextLink
                onClick={() => onOpenRecruiterProfile?.(item.recruiterProfileId)}
                sx={{ color: BLUE, fontWeight: 900 }}
              >
                {item.owner}
              </StageTextLink>
            ) : (
              <Box component="span">{item.owner}</Box>
            )
          ) : null}
        </Typography>

        <RejectionDetails item={item} compact />
        <InterviewScheduleInline candidateId={item.candidateId} jobId={item.jobId} stage={item.stageLabel} />

        <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 1.1, flexWrap: "wrap" }}>
          <StageActionButton
            disabled={!item.candidateId || moving}
            onClick={(event) => onOpenMoveStageMenu?.(event, item.candidateId)}
          >
            Move Stage
          </StageActionButton>
          <StageActionButton
            color={MUTED}
            disabled={!item.candidateId || moving}
            onClick={() => onRejectCandidate?.(item.candidateId)}
          >
            Reject
          </StageActionButton>
          {canDelete ? (
            <StageActionButton
              color={DANGER}
              disabled={!item.candidateId || moving}
              onClick={() => onDeleteCandidate?.(item.candidateId)}
            >
              Delete
            </StageActionButton>
          ) : null}
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: "Be Vietnam Pro",
          fontSize: 10.5,
          fontWeight: 900,
          color: MUTED,
          whiteSpace: "nowrap",
        }}
      >
        {item.leadStatus}
      </Typography>

      <Checkbox
        size="small"
        checked={checked}
        disabled={!item.candidateId || moving}
        onChange={() => onToggleSelect?.(item.candidateId)}
        sx={{
          p: 0,
          width: 18,
          height: 18,
          "& .MuiSvgIcon-root": { fontSize: 18 },
          color: "#D0D5DD",
          "&.Mui-checked": { color: PURPLE },
        }}
      />
    </Box>
  );
}

export default function JobDetailsStagesTab({
  data,
  selectedStage = "all",
  rows = [],
  onMoveCandidatesToStage,
  onDeleteCandidatesFromStage,
  onEditHeader,
  isAdminUser = false,
  canDeleteCandidates = isAdminUser,
  currentUserRole = "",
}) {
  const navigate = useNavigate();
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [recruiterUsers, setRecruiterUsers] = useState([]);
  const [moveStageMenuState, setMoveStageMenuState] = useState({
    anchorEl: null,
    candidateIds: [],
  });
  const [rejectDialogState, setRejectDialogState] = useState({
    open: false,
    candidateIds: [],
  });
  const [moveStageSaving, setMoveStageSaving] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [pendingCandidateIds, setPendingCandidateIds] = useState([]);
  const stageOrder = useMemo(() => STAGE_ORDER, []);

  const stageIcons = useMemo(
    () => ({
      New: <AccordionHeadingIcon stage="New" />,
      Applied: <AccordionHeadingIcon stage="Applied" />,
      Matched: <AccordionHeadingIcon stage="Matched" />,
      Sourced: <AccordionHeadingIcon stage="Sourced" />,
      Contacted: <AccordionHeadingIcon stage="Contacted" />,
      Screened: <AccordionHeadingIcon stage="Screened" />,
      Presented: <AccordionHeadingIcon stage="Presented" />,
      Interview: <AccordionHeadingIcon stage="Interview" />,
      Assessment: <AccordionHeadingIcon stage="Assessment" />,
      Offer: <AccordionHeadingIcon stage="Offer" />,
      Hired: <AccordionHeadingIcon stage="Hired" />,
      Rejected: <AccordionHeadingIcon stage="Rejected" />,
    }),
    []
  );
  const recruiterUsersByName = useMemo(
    () =>
      new Map(
        (Array.isArray(recruiterUsers) ? recruiterUsers : [])
          .map((user) => [normalizeLookupName(user?.name), user])
          .filter(([key]) => Boolean(key))
      ),
    [recruiterUsers]
  );

  const stageItems = useMemo(
    () =>
      (Array.isArray(rows) ? rows : []).map((row) =>
        mapRowToStageItem(row, recruiterUsersByName)
      ),
    [recruiterUsersByName, rows]
  );
  const stageItemsByCandidateId = useMemo(
    () =>
      new Map(
        stageItems
          .filter((item) => Number.isFinite(item?.candidateId) && item.candidateId > 0)
          .map((item) => [item.candidateId, item])
      ),
    [stageItems]
  );
  const selectedCandidateIdSet = useMemo(
    () => new Set(selectedCandidateIds),
    [selectedCandidateIds]
  );
  const pendingCandidateIdSet = useMemo(
    () => new Set(pendingCandidateIds),
    [pendingCandidateIds]
  );
  const actionSaving = moveStageSaving || deleteSaving;
  const canDeleteStageCandidates =
    Boolean(canDeleteCandidates) &&
    typeof onDeleteCandidatesFromStage === "function";

  useEffect(() => {
    let active = true;

    fetchRecruiterUsers({ apiBase: API_BASE })
      .then((users) => {
        if (!active) return;
        setRecruiterUsers(Array.isArray(users) ? users : []);
      })
      .catch(() => {
        if (!active) return;
        setRecruiterUsers([]);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const validCandidateIds = new Set(
      stageItems
        .map((item) => item.candidateId)
        .filter((candidateId) => Number.isFinite(candidateId) && candidateId > 0)
    );

    setSelectedCandidateIds((prev) =>
      prev.filter((candidateId) => validCandidateIds.has(candidateId))
    );
    setPendingCandidateIds((prev) =>
      prev.filter((candidateId) => validCandidateIds.has(candidateId))
    );
  }, [stageItems]);

  const toggleCandidateSelection = useCallback((candidateId) => {
    if (!Number.isFinite(candidateId) || candidateId <= 0) return;

    setSelectedCandidateIds((prev) =>
      prev.includes(candidateId)
        ? prev.filter((value) => value !== candidateId)
        : [...prev, candidateId]
    );
  }, []);

  const openMoveStageMenu = useCallback((anchorEl, candidateIds = []) => {
    if (actionSaving) return;

    const normalizedCandidateIds = Array.from(
      new Set(
        (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0)
      )
    );

    if (!anchorEl || !normalizedCandidateIds.length) return;

    setMoveStageMenuState({
      anchorEl,
      candidateIds: normalizedCandidateIds,
    });
  }, [actionSaving]);

  const closeMoveStageMenu = useCallback(() => {
    if (actionSaving) return;

    setMoveStageMenuState({
      anchorEl: null,
      candidateIds: [],
    });
  }, [actionSaving]);

  const handleOpenBulkMoveStageMenu = useCallback(
    (event) => {
      openMoveStageMenu(event.currentTarget, selectedCandidateIds);
    },
    [openMoveStageMenu, selectedCandidateIds]
  );

  const handleOpenCandidateMoveStageMenu = useCallback(
    (event, candidateId) => {
      event.preventDefault();
      event.stopPropagation();
      openMoveStageMenu(event.currentTarget, [candidateId]);
    },
    [openMoveStageMenu]
  );

  const openRejectDialog = useCallback(
    (candidateIds = []) => {
      if (actionSaving) return;

      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );

      if (!normalizedCandidateIds.length) return;

      setMoveStageMenuState({
        anchorEl: null,
        candidateIds: [],
      });
      setRejectDialogState({
        open: true,
        candidateIds: normalizedCandidateIds,
      });
    },
    [actionSaving]
  );

  const closeRejectDialog = useCallback(() => {
    if (moveStageSaving) return;

    setRejectDialogState({
      open: false,
      candidateIds: [],
    });
  }, [moveStageSaving]);

  const handleSelectMoveStage = useCallback(
    async (nextStage, candidateIds = moveStageMenuState.candidateIds) => {
      if (actionSaving) return;
      if (typeof onMoveCandidatesToStage !== "function") return;
      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );
      if (!normalizedCandidateIds.length) return;
      if (normalizeStageLabel(nextStage) === "Rejected") {
        openRejectDialog(normalizedCandidateIds);
        return;
      }

      setMoveStageSaving(true);
      setPendingCandidateIds(normalizedCandidateIds);

      try {
        await onMoveCandidatesToStage(normalizedCandidateIds, nextStage);
        setSelectedCandidateIds((prev) =>
          prev.filter((candidateId) => !normalizedCandidateIds.includes(candidateId))
        );
        setMoveStageMenuState({
          anchorEl: null,
          candidateIds: [],
        });
      } catch (error) {
        console.error("Failed to move job candidates to a new stage:", error);
      } finally {
        setMoveStageSaving(false);
        setPendingCandidateIds([]);
      }
    },
    [actionSaving, moveStageMenuState.candidateIds, onMoveCandidatesToStage, openRejectDialog]
  );

  const handleRejectCandidate = useCallback(
    async (candidateId) => {
      openRejectDialog([candidateId]);
    },
    [openRejectDialog]
  );

  const handleConfirmRejectDialog = useCallback(
    async ({ rejectedBy = "", rejectedByLabel = "", reason = "", note = "", disposition, decisionBy } = {}) => {
      if (typeof onMoveCandidatesToStage !== "function") return;

      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(rejectDialogState.candidateIds)
            ? rejectDialogState.candidateIds
            : []
          )
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );

      if (!normalizedCandidateIds.length) return;

      setMoveStageSaving(true);
      setPendingCandidateIds(normalizedCandidateIds);

      try {
        await onMoveCandidatesToStage(normalizedCandidateIds, "Rejected", {
          rejectedBy: rejectedBy || rejectedByLabel,
          rejectionReason: reason,
          rejectionNote: note,
          disposition, decisionBy,
        });
        setSelectedCandidateIds((prev) =>
          prev.filter((candidateId) => !normalizedCandidateIds.includes(candidateId))
        );
        setRejectDialogState({
          open: false,
          candidateIds: [],
        });
      } catch (error) {
        console.error("Failed to reject job candidates:", error);
        throw error;
      } finally {
        setMoveStageSaving(false);
        setPendingCandidateIds([]);
      }
    },
    [onMoveCandidatesToStage, rejectDialogState.candidateIds]
  );

  const handleDeleteCandidates = useCallback(
    async (candidateIds = []) => {
      if (!canDeleteStageCandidates || actionSaving) return;

      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );
      if (!normalizedCandidateIds.length) return;

      const candidateNames = normalizedCandidateIds
        .map((candidateId) => stageItemsByCandidateId.get(candidateId)?.name)
        .filter(Boolean);
      const confirmed = window.confirm(
        normalizedCandidateIds.length === 1
          ? `Delete ${candidateNames[0] || "this candidate"} from this job? This removes them from the stages tab.`
          : `Delete ${normalizedCandidateIds.length} candidates from this job? This removes them from the stages tab.`
      );
      if (!confirmed) return;

      setDeleteSaving(true);
      setPendingCandidateIds(normalizedCandidateIds);

      try {
        await onDeleteCandidatesFromStage(normalizedCandidateIds);
        setSelectedCandidateIds((prev) =>
          prev.filter((candidateId) => !normalizedCandidateIds.includes(candidateId))
        );
        setMoveStageMenuState({
          anchorEl: null,
          candidateIds: [],
        });
      } catch (error) {
        console.error("Failed to delete job candidates from this stage:", error);
      } finally {
        setDeleteSaving(false);
        setPendingCandidateIds([]);
      }
    },
    [
      actionSaving,
      canDeleteStageCandidates,
      onDeleteCandidatesFromStage,
      stageItemsByCandidateId,
    ]
  );

  const handleDeleteCandidate = useCallback(
    async (candidateId) => {
      await handleDeleteCandidates([candidateId]);
    },
    [handleDeleteCandidates]
  );

  const handleDeleteSelectedCandidates = useCallback(async () => {
    await handleDeleteCandidates(selectedCandidateIds);
  }, [handleDeleteCandidates, selectedCandidateIds]);

  const handleOpenCandidateProfile = useCallback(
    (candidateId) => {
      const normalizedCandidateId = Number(candidateId || 0);
      if (!Number.isFinite(normalizedCandidateId) || normalizedCandidateId <= 0) return;

      navigate({
        pathname: "/candidates",
        search: `?candidateId=${encodeURIComponent(String(normalizedCandidateId))}&tab=details`,
      });
    },
    [navigate]
  );

  const handleOpenCompanyProfile = useCallback(
    (companyId, companyName = "") => {
      const normalizedCompanyId = Number(companyId || 0);
      if (!Number.isFinite(normalizedCompanyId) || normalizedCompanyId <= 0) return;

      const resolvedCompanyName = String(companyName || "").trim() || "Company";
      const payload = buildOpenCompanyNavPayload({
        company: {
          id: normalizedCompanyId,
          company_id: normalizedCompanyId,
          companyId: normalizedCompanyId,
          name: resolvedCompanyName,
          company_name: resolvedCompanyName,
        },
        defaultTab: "details",
        returnListTab: "companies",
        entityType: "company",
      });
      if (!payload) return;

      try {
        sessionStorage.setItem(NAV_CACHE_KEY, JSON.stringify(payload));
      } catch {
        // Ignore session storage failures.
      }

      navigate(
        {
          pathname: "/companies",
          search: `?companyId=${encodeURIComponent(String(normalizedCompanyId))}&tab=details&entityType=company&returnTab=companies`,
        },
        {
          state: payload,
        }
      );
    },
    [navigate]
  );

  const handleOpenRecruiterProfile = useCallback(
    (recruiterProfileId) => {
      const normalizedProfileId = Number(recruiterProfileId || 0);
      if (!Number.isFinite(normalizedProfileId) || normalizedProfileId <= 0) return;

      navigate(`/profile/${encodeURIComponent(String(normalizedProfileId))}`);
    },
    [navigate]
  );

  const allStages = useMemo(
    () =>
      stageOrder.map((key) => ({
        key,
        icon: stageIcons[key],
        items: stageItems.filter((item) => item.stageLabel === key),
      })),
    [stageIcons, stageItems, stageOrder]
  );

  const stages = allStages;

  const defaultExpanded = useMemo(
    () =>
      stages.reduce((acc, stage) => {
        acc[stage.key] = stage.items.length > 0;
        return acc;
      }, {}),
    [stages]
  );

  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <JobTabHeader
        companyLogo={data.companyLogo}
        companyName={data.companyName}
        companyDomain={data.companyDomain}
        profileData={data}
        onEdit={onEditHeader}
        sx={{ mb: 1.25 }}
      />

      <Box sx={{ pt: 3, pb: 3, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={<MoveStageActionIcon size={16} />}
          endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />}
          disabled={
            actionSaving ||
            !selectedCandidateIds.length ||
            typeof onMoveCandidatesToStage !== "function"
          }
          sx={MOVE_STAGE_BULK_BUTTON_SX}
          onClick={handleOpenBulkMoveStageMenu}
        >
          Move Stage
        </Button>
        {canDeleteStageCandidates ? (
          <Button
            variant="outlined"
            disabled={actionSaving || !selectedCandidateIds.length}
            onClick={handleDeleteSelectedCandidates}
            sx={{
              textTransform: "none",
              fontFamily: "Be Vietnam Pro",
              fontWeight: 900,
              fontSize: 11,
              borderRadius: 999,
              px: 1.5,
              py: 0.7,
              borderColor: "#FECACA",
              color: DANGER,
              backgroundColor: "#fff",
              "&:hover": {
                borderColor: "#FCA5A5",
                backgroundColor: "#FEF2F2",
              },
            }}
          >
            Delete
          </Button>
        ) : null}
      </Box>

      <RecruitingStageAccordionList
        candidates={stageItems} jobId={data?.jobId} refreshKey={rows}
        pageSize={10}
        stages={stages}
        defaultExpanded={defaultExpanded}
        defaultView="grid"
        renderGrid={({ items }) => (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.25,
            }}
          >
            {items.map((item) => (
              <StageCard
                key={item.id}
                item={item}
                checked={!!(item.candidateId && selectedCandidateIdSet.has(item.candidateId))}
                moving={!!(item.candidateId && pendingCandidateIdSet.has(item.candidateId))}
                canDelete={canDeleteStageCandidates}
                onToggleSelect={toggleCandidateSelection}
                onOpenMoveStageMenu={handleOpenCandidateMoveStageMenu}
                onRejectCandidate={handleRejectCandidate}
                onDeleteCandidate={handleDeleteCandidate}
                onOpenCandidateProfile={handleOpenCandidateProfile}
                onOpenCompany={handleOpenCompanyProfile}
                onOpenRecruiterProfile={handleOpenRecruiterProfile}
              />
            ))}
          </Box>
        )}
        renderList={({ items }) => (
          <Box sx={{ display: "grid", gap: 1 }}>
            {items.map((item) => (
              <StageRow
                key={item.id}
                item={item}
                checked={!!(item.candidateId && selectedCandidateIdSet.has(item.candidateId))}
                moving={!!(item.candidateId && pendingCandidateIdSet.has(item.candidateId))}
                canDelete={canDeleteStageCandidates}
                onToggleSelect={toggleCandidateSelection}
                onOpenMoveStageMenu={handleOpenCandidateMoveStageMenu}
                onRejectCandidate={handleRejectCandidate}
                onDeleteCandidate={handleDeleteCandidate}
                onOpenCandidateProfile={handleOpenCandidateProfile}
                onOpenCompany={handleOpenCompanyProfile}
                onOpenRecruiterProfile={handleOpenRecruiterProfile}
              />
            ))}
          </Box>
        )}
      />

      <Menu
        anchorEl={moveStageMenuState.anchorEl}
        open={Boolean(moveStageMenuState.anchorEl)}
        onClose={closeMoveStageMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
      >
        {MOVE_STAGE_OPTIONS.map((stageOption) => (
          <MenuItem
            key={stageOption}
            disabled={actionSaving}
            onClick={() => handleSelectMoveStage(stageOption)}
            sx={{
              fontFamily: "Be Vietnam Pro",
              fontSize: 12,
              fontWeight: 800,
              color: TEXT,
            }}
          >
            {stageOption}
          </MenuItem>
        ))}
      </Menu>

      <RejectedStageDialog
        jobSpecific={data?.entityType !== "lead"}
        open={rejectDialogState.open}
        loading={moveStageSaving}
        candidateCount={rejectDialogState.candidateIds.length}
        currentUserRole={currentUserRole}
        onClose={closeRejectDialog}
        onSubmit={handleConfirmRejectDialog}
      />
    </Box>
  );
}
